// Pattern: docs/design-patterns/frontend/state-management — server state in TanStack Query

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import * as refArchApi from '~/services/api/reference-architectures';

import type { RefArchDetail, RefArchManifest, RefArchManifestEntry } from '~/types';

import { useAuth } from './useAuth';

export const refArchKeys = {
  all: ['reference-architectures'] as const,
  manifest: () => [...refArchKeys.all, 'manifest'] as const,
  detail: (id: string) => [...refArchKeys.all, 'detail', id] as const,
};

const THIRTY_MINUTES = 30 * 60 * 1000;

export function useRefArchDetail(refArchId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  refArch: RefArchDetail | null;
} {
  const { loading, user } = useAuth();

  const {
    data: refArch,
    error,
    isLoading,
  } = useQuery({
    queryKey: refArchKeys.detail(refArchId ?? ''),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by `enabled: !!refArchId`
    queryFn: () => refArchApi.getRefArch(refArchId!),
    enabled: !loading && !!user && !!refArchId,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    refArch: refArch || null,
    isLoading,
    error,
  };
}

export function useRefArchManifest(): {
  error: unknown;
  isLoading: boolean;
  manifest: RefArchManifest | null;
} {
  const { loading, user } = useAuth();

  const {
    data: manifest,
    error,
    isLoading,
  } = useQuery({
    queryKey: refArchKeys.manifest(),
    queryFn: refArchApi.getRefArchManifest,
    enabled: !loading && !!user,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    manifest: manifest || null,
    isLoading,
    error,
  };
}

export function useRefArchsForPattern(patternId: string): {
  refArchs: RefArchManifestEntry[];
} {
  const { manifest } = useRefArchManifest();

  const refArchs = useMemo(() => {
    if (!manifest) return [];
    return manifest.reference_architectures.filter(ra => ra.pattern_ids.includes(patternId));
  }, [manifest, patternId]);

  return { refArchs };
}
